// Territoires des partenaires (spec § 3) : pays, région facultative, zones (groupes de pays fixés dans la base) et
// licence monde. La base applique ces règles (`can_see_project`) ; ces fonctions les reprennent pour l'affichage.
import { useQuery } from "@tanstack/react-query";
import type { useTranslate } from "ra-core";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { SubscriptionRequest, UserTerritory } from "../types";
import { countryName } from "./projectUtils";

export type Zone = { code: string; countries: string[] };

export type PartnerOffer = {
  partner_offer?: string | null;
  partner_world?: boolean | null;
};

type Territory = {
  country?: string | null;
  region?: string | null;
  zone?: string | null;
};

/** Fiches recensées dans un pays, toute la base (fonction `coverage_estimate`). */
export type CoverageRow = {
  country: string;
  projects: number;
  projects_30d: number;
  high_score_30d: number;
};

export type Coverage = Omit<CoverageRow, "country"> & { countries: number };

/** Même règle que la base : un invité ne voit rien par ses territoires ; un abonné voit le monde, ses zones et ses
 * pays (la région, si le territoire en précise une, comparée sans tenir compte de la casse). */
export const seesByTerritory = (
  partner: PartnerOffer,
  territories: Territory[],
  zones: Zone[],
  project: { country?: string | null; region?: string | null },
) => {
  if (partner.partner_offer !== "subscriber") return false;
  if (partner.partner_world) return true;
  if (!project.country) return false;
  const region = project.region?.trim().toLowerCase();
  return territories.some((territory) =>
    territory.zone
      ? !!zones
          .find((zone) => zone.code === territory.zone)
          ?.countries.includes(project.country!)
      : territory.country === project.country &&
        (!territory.region || territory.region.trim().toLowerCase() === region),
  );
};

/** Total des fiches couvertes par des zones et des pays (chaque pays compté une fois), ou par le monde. */
export const coverageOf = (
  rows: CoverageRow[],
  zones: Zone[],
  selection: { zones: string[]; countries: string[] } | "world",
): Coverage => {
  const countries =
    selection === "world"
      ? null
      : new Set([
          ...selection.countries,
          ...zones
            .filter((zone) => selection.zones.includes(zone.code))
            .flatMap((zone) => zone.countries),
        ]);
  const covered = rows.filter(
    (row) => !countries || countries.has(row.country),
  );
  return {
    countries: covered.length,
    projects: covered.reduce((sum, row) => sum + Number(row.projects), 0),
    projects_30d: covered.reduce(
      (sum, row) => sum + Number(row.projects_30d),
      0,
    ),
    high_score_30d: covered.reduce(
      (sum, row) => sum + Number(row.high_score_30d),
      0,
    ),
  };
};

/** Zones et leurs pays, dans l'ordre de la base. */
export const useZones = () =>
  useQuery({
    queryKey: ["territory_zones"],
    queryFn: async (): Promise<Zone[]> => {
      const supabase = getSupabaseClient();
      const [{ data: zones, error }, { data: countries, error: error2 }] =
        await Promise.all([
          supabase.from("territory_zones").select("code").order("position"),
          supabase.from("territory_zone_countries").select("zone, country"),
        ]);
      if (error || error2) throw error ?? error2;
      return (zones ?? []).map(({ code }) => ({
        code,
        countries: (countries ?? [])
          .filter((row) => row.zone === code)
          .map((row) => row.country)
          .sort(),
      }));
    },
    staleTime: Infinity,
  });

type Translate = ReturnType<typeof useTranslate>;

/** Libellé d'un territoire : zone, ou pays et région. */
export const territoryLabel = (
  territory: UserTerritory,
  locale: string,
  translate: Translate,
) =>
  territory.zone
    ? translate(`resources.projects.zones.${territory.zone}`)
    : territory.region
      ? `${countryName(territory.country, locale)} · ${territory.region}`
      : countryName(territory.country, locale);

/** Fiches recensées par pays, sur toute la base : des comptes seulement. */
export const useCoverage = (enabled = true) =>
  useQuery({
    queryKey: ["coverage_estimate"],
    enabled,
    queryFn: async (): Promise<CoverageRow[]> => {
      const { data, error } =
        await getSupabaseClient().rpc("coverage_estimate");
      if (error) throw error;
      return data ?? [];
    },
  });

/** Libellé d'une demande d'abonnement : formule, pays ou zone, engagement. */
export const subscriptionLabel = (
  request: Pick<
    SubscriptionRequest,
    "plan" | "commitment" | "country" | "zone"
  >,
  locale: string,
  translate: Translate,
) =>
  [
    translate(`resources.projects.pricing.plans.${request.plan}.name`),
    request.zone
      ? translate(`resources.projects.zones.${request.zone}`)
      : request.country
        ? countryName(request.country, locale)
        : null,
    translate(
      `resources.projects.pricing.${request.commitment}`,
    ).toLocaleLowerCase(locale),
  ]
    .filter(Boolean)
    .join(" · ");
